import { useState } from 'react'
import { ExternalLink, Phone, RefreshCw } from 'lucide-react'
import { Button, Checkbox, Chip, Drawer, Select, Textarea, toast } from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import {
  pdfUrl,
  useLots,
  usePreShipCall,
  useSetFlags,
  useSetLot,
  useSync,
  useUvivaInstruction,
} from '../api/queries'
import type { InspecPo } from '../api/types'
import { fmtDate, fmtKg, fmtLb } from '../utils/format'

interface Props {
  po: InspecPo | null
  canEdit: boolean
  onClose: () => void
}

const LOT_ODOO_NOTE: Record<string, string> = {
  ok: 'Lot saved and held in Odoo.',
  odoo_module_missing:
    'Lot saved here. Odoo will hold it once the Dedicated Lot module is installed.',
  not_in_odoo: 'Lot saved. It goes to Odoo when the order is created.',
  no_lines: 'Lot saved. The Odoo order has no lines yet.',
}

/** One InSpec PO: shipments + pre-ship calls, the dedicated lot, flags,
 *  problems found by the reconciler, history; the PO PDF alongside. */
export function PoDrawer({ po, canEdit, onClose }: Props) {
  const [callFor, setCallFor] = useState<number | null>(null)
  const [callNote, setCallNote] = useState('')
  const [lotChoice, setLotChoice] = useState('')
  const lots = useLots(!!po && canEdit)
  const setLot = useSetLot()
  const setFlags = useSetFlags()
  const sync = useSync()
  const instruct = useUvivaInstruction()
  const call = usePreShipCall()

  if (!po) return null

  const onSaveLot = (lotName: string | null) =>
    setLot.mutate(
      { po: po.po_number, lot_name: lotName },
      {
        onSuccess: (r) => {
          toast.success(lotName ? (LOT_ODOO_NOTE[r.odoo] ?? 'Lot saved.') : 'Lot cleared.')
          setLotChoice('')
        },
        onError: (e) => toast.danger(e instanceof Error ? e.message : 'Could not save the lot'),
      },
    )

  const free = (lots.data?.items ?? []).filter(
    (l) => !l.assigned_to || l.assigned_to === po.po_number,
  )

  return (
    <Drawer
      open={!!po}
      onOpenChange={(o) => !o && onClose()}
      size="xl"
      surface="form"
      ident={po.po_number}
      title={`InSpec ${po.po_number}${po.revision > 1 ? ` (revision ${po.revision})` : ''}`}
      meta={`${fmtKg(po.qty_kg)} / ${fmtLb(po.qty_lb)}, PO date ${fmtDate(po.po_date)}, expected ${fmtDate(po.expected_date)}`}
      headerActions={
        <>
          {po.tentative ? <Chip variant="warning">Subject to change</Chip> : null}
          {po.cancelled ? <Chip variant="neutral">Cancelled</Chip> : null}
          {po.odoo_so_id ? (
            <a
              href={odooUrl('sale.order', po.odoo_so_id)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-link hover:underline"
            >
              {po.odoo_so_name} <ExternalLink size={12} />
            </a>
          ) : null}
        </>
      }
      footer={
        canEdit ? (
          <Button
            variant="secondary"
            loading={sync.isPending}
            onClick={() =>
              sync.mutate(po.po_number, {
                onSuccess: (r) => toast.success(`Odoo: ${String(r.odoo.action ?? 'done')}`),
                onError: (e) => toast.danger(e instanceof Error ? e.message : 'Sync failed'),
              })
            }
          >
            <RefreshCw size={14} /> {po.odoo_so_id ? 'Push to Odoo again' : 'Create Odoo order'}
          </Button>
        ) : undefined
      }
      splitContent={
        po.source_document_id ? (
          <iframe
            title={`${po.po_number} PDF`}
            src={pdfUrl(po.po_number)}
            className="h-full min-h-[600px] w-full"
          />
        ) : (
          <p className="p-4 text-sm text-tertiary">No PDF stored for this PO.</p>
        )
      }
    >
      <section className="mb-6">
        <h3 className="text-role-section-label mb-2">1 Shipments</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-subtle text-left text-secondary">
              <th className="py-1">#</th>
              <th>Qty</th>
              <th>ETA</th>
              <th>Delivered</th>
              <th>Pre-ship call</th>
            </tr>
          </thead>
          <tbody>
            {po.releases.map((r) => (
              <tr key={r.id} className="border-b border-subtle align-top">
                <td className="py-2">{r.seq}</td>
                <td>
                  {fmtKg(r.qty_kg)}
                  <div className="text-xs text-tertiary">{fmtLb(r.qty_lb)}</div>
                </td>
                <td>{fmtDate(r.eta)}</td>
                <td>
                  {r.qty_delivered_kg > 0 ? fmtKg(r.qty_delivered_kg) : '-'}
                  {r.shipped_lot_names.length ? (
                    <div className="text-xs text-tertiary">
                      lot {r.shipped_lot_names.join(', ')}
                    </div>
                  ) : null}
                </td>
                <td>
                  {r.pre_ship_call_at ? (
                    <div>
                      <Chip size="xs" variant="success">
                        Called {new Date(r.pre_ship_call_at).toLocaleDateString()}
                      </Chip>
                      <div className="text-xs text-tertiary">
                        {r.pre_ship_call_by}
                        {r.pre_ship_call_note ? `: ${r.pre_ship_call_note}` : ''}
                      </div>
                      {canEdit ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => call.mutate({ releaseId: r.id, clear: true })}
                        >
                          Undo
                        </Button>
                      ) : null}
                    </div>
                  ) : canEdit && callFor === r.id ? (
                    <div className="flex flex-col gap-2">
                      <Textarea
                        aria-label="Call note"
                        rows={2}
                        placeholder="e.g. Spoke with Kim, OK to ship Dec 4"
                        value={callNote}
                        onChange={(e) => setCallNote(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          loading={call.isPending}
                          onClick={() =>
                            call.mutate(
                              { releaseId: r.id, note: callNote },
                              {
                                onSuccess: () => {
                                  toast.success('Call logged')
                                  setCallFor(null)
                                },
                                onError: (e) =>
                                  toast.danger(
                                    e instanceof Error ? e.message : 'Could not log the call',
                                  ),
                              },
                            )
                          }
                        >
                          Save call
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setCallFor(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : canEdit ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setCallNote('')
                        setCallFor(r.id)
                      }}
                    >
                      <Phone size={14} /> Log call
                    </Button>
                  ) : (
                    <span className="text-tertiary">Not called</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mb-6">
        <h3 className="text-role-section-label mb-2">2 Dedicated lot</h3>
        {po.lot_name ? (
          <p className="text-sm">
            <span className="text-role-data-primary">{po.lot_name}</span>{' '}
            <span className="text-tertiary">
              {po.lot_source === 'uviva_shipment'
                ? 'from the Uviva shipment'
                : `set by ${po.lot_assigned_by ?? 'ops'}`}
              {po.lot_assigned_at ? `, ${new Date(po.lot_assigned_at).toLocaleDateString()}` : ''}
            </span>
          </p>
        ) : (
          <p className="text-sm text-secondary">
            No lot yet. The first lot Uviva ships against this order becomes its lot; you can also
            set it here.
          </p>
        )}
        {canEdit && !po.cancelled ? (
          <div className="mt-2 flex items-center gap-2">
            <Select
              aria-label="Lot"
              value={lotChoice}
              onChange={(e) => setLotChoice(e.target.value)}
              disabled={lots.isLoading}
            >
              <option value="">
                {lots.isLoading ? 'Loading lots…' : 'Choose an Eclipse Z1 lot'}
              </option>
              {free.map((l) => (
                <option key={l.id} value={l.name}>
                  {l.name} ({fmtKg(l.qty_on_hand_kg)} on hand, created {fmtDate(l.created)})
                </option>
              ))}
            </Select>
            <Button
              variant="primary"
              size="sm"
              disabled={!lotChoice}
              loading={setLot.isPending}
              onClick={() => onSaveLot(lotChoice)}
            >
              {po.lot_name ? 'Change lot' : 'Assign lot'}
            </Button>
            {po.lot_name ? (
              <Button
                variant="ghost"
                size="sm"
                loading={setLot.isPending}
                onClick={() => onSaveLot(null)}
              >
                Clear
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="mb-6">
        <h3 className="text-role-section-label mb-2">3 Uviva PO</h3>
        {po.uviva_pos.length === 0 ? (
          <p className="text-sm text-secondary">
            No Uviva PO yet. It appears once the order is confirmed in Odoo and the purchase is
            created.
          </p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {po.uviva_pos.map((u) => (
              <li key={u.id} className="flex items-center gap-2">
                <a
                  href={odooUrl('purchase.order', u.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link hover:underline"
                >
                  {u.name}
                </a>
                {u.instruction ? (
                  <Chip size="xs" variant="success">
                    Lot instruction on PO
                  </Chip>
                ) : (
                  <Chip size="xs" variant="warning">
                    No lot instruction
                  </Chip>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && po.uviva_pos.some((u) => !u.instruction) ? (
          <Button
            variant="secondary"
            size="sm"
            className="mt-2"
            loading={instruct.isPending}
            onClick={() =>
              instruct.mutate(po.po_number, {
                onSuccess: (r) =>
                  toast.success(
                    r.added.length ? `Instruction added to ${r.added.join(', ')}` : 'Already there',
                  ),
                onError: (e) =>
                  toast.danger(e instanceof Error ? e.message : 'Could not update the Uviva PO'),
              })
            }
          >
            Add lot instruction to Uviva PO
          </Button>
        ) : null}
      </section>

      {po.issues.length > 0 ? (
        <section className="mb-6">
          <h3 className="text-role-section-label mb-2">4 Problems</h3>
          <ul className="list-disc pl-5 text-sm text-danger-text">
            {po.issues.map((i, n) => (
              <li key={n}>
                {i.detail}
                {canEdit && i.kind === 'uviva_lot_suggested' && i.lot_name && !po.lot_name ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="ml-2"
                    loading={setLot.isPending}
                    onClick={() => onSaveLot(i.lot_name!)}
                  >
                    Assign {i.lot_name}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {canEdit ? (
        <section className="mb-6">
          <h3 className="text-role-section-label mb-2">{po.issues.length ? '5' : '4'} Status</h3>
          <div className="flex flex-col gap-2">
            <Checkbox
              label="Subject to change (no customer orders behind it yet)"
              checked={po.tentative}
              onChange={(e) => setFlags.mutate({ po: po.po_number, tentative: e.target.checked })}
            />
            <Checkbox
              label="Cancelled by InSpec (cancel the Odoo order separately)"
              checked={po.cancelled}
              onChange={(e) => setFlags.mutate({ po: po.po_number, cancelled: e.target.checked })}
            />
          </div>
        </section>
      ) : null}

      <section>
        <h3 className="text-role-section-label mb-2">History</h3>
        <ol className="flex flex-col gap-1 text-sm">
          {[...po.stage_history].reverse().map((h, n) => (
            <li key={n} className="text-secondary">
              <span className="text-tertiary">{new Date(h.at).toLocaleString()}</span>{' '}
              {h.event.replaceAll('_', ' ')}
              {h.note ? `: ${h.note}` : ''} <span className="text-tertiary">({h.actor})</span>
            </li>
          ))}
        </ol>
      </section>
    </Drawer>
  )
}
